import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Camera, Users, CheckCircle, Lightbulb } from "lucide-react";
import heroImage from "@/assets/blog/camera-productivity-hero.jpg";
import studyCommunityImage from "@/assets/blog/study-community.jpg";

const tocItems = [
  { id: "study-with-me-revolution", title: 'The "Study with Me" Revolution' },
  { id: "key-productivity-boosts", title: "Key Productivity Boosts" },
  { id: "getting-started", title: "Practical Steps on How to Get Started" },
  { id: "conclusion", title: "Conclusion" },
];

const HowTurningOnMyCameraBoostedMyProductivity = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="How Turning on my Camera Boosted my Productivity"
        description="Discover how turning on your camera in online study rooms can transform your productivity. Learn about the being visible effect and practical tips to get started."
        keywords="online study rooms, productivity tips, study with me, camera on study, accountability, focus techniques, CSW"
        canonicalUrl="/blog/how-turning-on-my-camera-boosted-my-productivity/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Productivity
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>December 7, 2024</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              How Turning on my Camera Boosted my Productivity
            </h1>

            {/* Featured Image */}
            <img 
              src={heroImage} 
              alt="Cozy study desk with laptop showing video call with people studying together" 
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
            
              {/* Lead Paragraph */}
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                I used to think that I could never study at home. That's because when I'm in my room, there's no one watching and no pressure to push. My room was therefore my biggest productivity trap. When you're there, endless distractions equals you drifting out of focus, and procrastination ends up becoming your roommate.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                I started learning about online study rooms when I saw a viral <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TikTok</a> video about someone joining one, albeit not to study but apparently to look at attractive people (LOL). Out of whim, I decided to join a study room through a website called <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW</a>.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                At first, I was just people watching. When I finally did turn on my camera, it felt a bit awkward. It felt like I was inviting an invisible audience to stare at me while I study. However, in the end it didn't really matter as much as I thought it would. It wasn't pressuring at all. There was no one who judged me or checked up on me, it felt like we were just sharing some quiet focus in real time. Going into online study rooms and adding them to my productivity method helped me become more accountable. Surprisingly, it boosted my motivation, concentration, and overall work rhythm. Funny enough, it turns out there's actually solid science behind a simple productivity hack. Apparently, when you turn on your camera, even if you're not chatting or interacting with anyone, your brain begins to pick up subtle social cues that make a huge difference.
              </p>

              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                Before that, let's talk about the "Study With Me" Revolution.
              </p>

              {/* Mobile TOC */}
              <div className="lg:hidden mb-12">
                <BlogTableOfContents items={tocItems} />
              </div>

            {/* Section 1 */}
            <h2 id="study-with-me-revolution" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              The "Study with Me" Revolution
            </h2>

            {/* Study With Me Image */}
            <img 
              src={studyCommunityImage} 
              alt="Community of students studying together virtually through video calls" 
              className="w-full aspect-video object-cover rounded-xl mb-10"
            />

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              I started digging deeper and found a whole community of people doing the same thing. I found out about "study with me" videos and online study rooms. I found out about platforms like <a href="https://www.studystream.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">StudyStream</a>, <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CSW</a>, and <a href="https://www.studytogether.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Study Together</a> which offer round-the-clock study rooms. These aren't just online study rooms but also workspaces where you can add your notes, to-do lists, and pomodoro timers. At least for CSW. Learn more about <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>the science behind "Study With Me"</Link> and discover <Link to="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>the research on why studying on camera actually helps you get more done</Link>.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              These virtual study rooms allow anyone to drop in anytime to get work done alongside others who are just as serious about their goals. It's like having a library or café on your laptop that never closes. Explore our <Link to="/study-stream/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>Study Stream</Link> feature to get started.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              The best part of course is that oftentimes these rooms operate 24/7. So whichever part of the world you are, there's always someone in that virtual space that keeps it alive and productive. It's the perfect setup for anyone who's a night owl or juggling irregular hours or maybe just craving that extra push. Discover <Link to="/blog/benefits-of-online-learning-community/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>the benefits of online learning communities</Link>.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              So, here's how turning on my camera became an absolute game changer.
            </p>

            {/* Section 2 */}
            <h2 id="key-productivity-boosts" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Key Productivity Boosts
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-10">
              Ever since I've been joining 24/7 online study rooms, I've taken some time to write about the benefits that I'm actually getting:
            </p>

            {/* Benefit Cards */}
            <div className="space-y-8 mb-12">
              <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Camera className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-display font-bold mb-4">Increased Accountability</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                      Knowing that others can see you creates a sense of accountability. Basically, you're less likely to zone out or multitask because your brain picks up with the idea that you could be seen at any moment. This is what people call the "being visible" effect, which nudges people to stay more focused and engaged. Imagine if you're in a library. You're more likely to study than nap, especially when you see others studying as well. Research shows that <Link to="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline" onClick={() => window.scrollTo(0, 0)}>studying on camera leverages social accountability, pre-commitment, and body doubling</Link> to dramatically improve focus.
                    </p>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                      Studies show that the "being visible" effect nudges people to stay more focused and engaged, much like how working in a bustling library makes it easier to resist distractions. We're social creatures, even when we think we aren't.
                    </p>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      Humans are social creatures, and our brains thrive on those quiet, shared moments of effort. That feeling of "we're doing this together," even through a screen, boosts motivation and keeps procrastination at bay. For me, it felt like having a productive roommate.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-display font-bold mb-4">A Boost in Motivation</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      Okay hear me out. You're probably wondering, well how in the hell does it boost your motivation when we're just turning on our cameras? For starters, when you see other people studying and working, you're also most likely to follow them as well. There's ultimately something motivating about seeing other people quietly working away, even when you don't talk or know them. For me, that sense of community really helped me push through moments when my energy was depleting because it showed me that I wasn't alone in the struggle.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-display font-bold mb-4">Helped me Build Consistent Routines</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      I don't know about others, but having 24/7 online study rooms made it easier for me to build consistent routines. I usually go into 24/7 online study rooms when I'm actually studying. Like studying with only pen and paper or flashcards, one where I don't necessarily have to use any online resources. That way, when I go into focus mode, I would just have my laptop screen with only the online study room in front of me.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors">
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-display font-bold mb-4">Great Community</h3>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                      When you go to online study rooms almost everyday, oftentimes you see the same people who you saw yesterday. This is especially true for me. I tend to join around the same time and usually with an existing room. Because of that, I tend to see the same people that I saw yesterday or days before that.
                    </p>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground mb-4">
                      In a way, this really helped me become more comfortable with online rooms. Aside from that, it felt like if I didn't come in everyday, I was letting everyone down (LOL). They definitely don't know me and I also don't know them, but seeing the same people everyday and doing quiet study definitely made me more committed.
                    </p>
                    <p className="text-lg leading-relaxed font-body text-muted-foreground">
                      Also, it showed me that even though we don't know each other and there's literally no interaction, online platforms such as CSW allow you to build an invisible community.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Community Image */}
            <img 
              src={heroImage} 
              alt="Virtual study room with participants on video call" 
              className="w-full aspect-[16/9] object-cover rounded-xl my-12"
            />

            {/* Section 3 */}
            <h2 id="getting-started" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Practical Steps on How to Get Started
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-10">
              So, I understand that actually joining a platform and turning on your camera is easier said than done. So here's some practical steps that you can get started without feeling awkward or overwhelmed:
            </p>

            <div className="space-y-8 mb-12">
              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Pick your platform</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    There's a lot of platforms out there but I generally use CSW because it's free. Aside from it being a 24/7 study online room, it also has a lot of productivity tools that you can use. Like it has a to-do list, a pomodoro timer, a notepad where you can put your notes in and so many other things.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Set up your Space</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    Choose a distraction-free space. Being in your room is okay, but I tend to avoid the bed or any spaces that would make me too comfortable. Setting up your space doesn't have to be complicated, but I like the idea of making my place look extra clean and aesthetically pleasing as if I'm the one who's shooting a study with me video.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Test Your Devices</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    Make sure your internet connection is stable and your camera works. You absolutely don't need a fancy set-up and oftentimes platforms automatically mute you to avoid interruptions when you join.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  4
                </div>
              <div>
                  <h3 className="font-display font-bold text-xl mb-3">Try to Mix in Structured Study</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    To keep myself organized, I tend to mix in structured study methods as well like the <a href="https://pomotime.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">pomodoro technique</a>. CSW has a built-in pomodoro timer which allows you to use it while you're in a study room. This helps build rhythm and make longer sessions easier to stick with.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                  5
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-3">Start Small!</h3>
                  <p className="text-lg leading-relaxed font-body text-muted-foreground">
                    It's okay to feel overwhelmed or anxious when it comes to this. Don't worry about being "perfect" at first. In fact, when I first started, I just turned off my camera and it was only in the next few sessions that I eventually did turn it on. Over time, showing up on camera feels natural, especially when you see other people doing the same thing.
                  </p>
                </div>
              </div>
            </div>

            {/* Workspace Setup Image */}
            <img 
              src={studyCommunityImage} 
              alt="Study workspace setup with virtual study community" 
              className="w-full aspect-[16/9] object-cover rounded-xl my-12"
            />

            {/* Conclusion */}
            <h2 id="conclusion" className="text-3xl md:text-4xl font-display font-bold mt-16 mb-8 tracking-tight">
              Conclusion
            </h2>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              At the end of the day, turning on my camera didn't just boost my productivity, it also transformed how I view study online communities. Even if it's just a quiet virtual community, for me it created the right atmosphere when it comes to ensuring balancing accountability and motivation. Whether you're a night owl tackling reading at 2 a.m. or grinding through a final project, by simply turning your camera on it might lead you to ace that test you've been preparing!
            </p>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-10 md:p-12 my-16 text-center">
              <h3 className="text-2xl md:text-3xl font-display font-bold mb-5">Ready to Boost Your Productivity?</h3>
              <p className="text-lg font-body text-muted-foreground mb-8 max-w-xl mx-auto">
                Join thousands of students and professionals in our 24/7 online study rooms. It's completely free!
              </p>
              <a 
                href="https://app.csw.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-10 py-4 bg-primary text-primary-foreground font-semibold font-body rounded-xl hover:opacity-90 transition-all text-lg"
              >
                Join CSW For Free
              </a>
            </div>

            {/* Related Articles Section */}
            <div className="mt-20">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-10 tracking-tight">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-1 gap-6">
                <Link 
                  to="/blog/how-to-stop-procrastination/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3">
                      <img 
                        src={heroImage} 
                        alt="Stop procrastination productivity tips" 
                        className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 md:w-2/3 flex flex-col justify-center">
                      <span className="text-sm text-primary font-medium font-body mb-2">Productivity</span>
                      <h3 className="text-xl md:text-2xl font-display font-bold mb-3 group-hover:text-primary transition-colors">
                        How to Stop Procrastination
                      </h3>
                      <p className="text-muted-foreground font-body line-clamp-2">
                        Learn practical strategies to overcome procrastination. Understand why you procrastinate and discover gentle, effective techniques to reclaim your time.
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>

          </div>
          
          {/* Floating Sticky TOC - Desktop only */}
          <aside className="hidden lg:block fixed right-8 xl:right-16 top-24 z-40">
            <BlogTableOfContents items={tocItems} collapsible />
          </aside>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default HowTurningOnMyCameraBoostedMyProductivity;
